import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  deleteAppFeature,
  getAdminDevices,
  getAppFeatures,
  getAppSettings,
  getUsers,
  updateAppFeature,
  updateAppSettings,
} from '../api/admin.js';
import { MultiSelectDropdown, type MultiSelectOption } from '../components/MultiSelectDropdown.js';
import type {
  AdminAppFeature,
  AppFeatureTargets,
  AppFeatureTargetingMode,
} from '@m8-app/contracts/rest/app-config.js';

const TABS = [
  { key: 'stair', label: 'M8 (Stair)' },
  { key: 'ceiling', label: 'Ceiling' },
  { key: 'lift', label: 'Lift' },
  { key: 'global', label: 'Global' },
] as const;

// Mirrors AppRoleSchema — the panel has no role catalog endpoint because the
// hierarchy is a contracts enum, not data.
const ROLE_OPTIONS: MultiSelectOption[] = [
  { id: 'super_admin', label: 'Super Admin' },
  { id: 'admin', label: 'Admin' },
  { id: 'installer', label: 'Installer' },
  { id: 'main_user', label: 'Main User' },
  { id: 'sub_main_user', label: 'Sub Main User' },
  { id: 'member', label: 'Member' },
  { id: 'sub_member', label: 'Sub Member' },
  { id: 'production_test', label: 'Production Tester' },
];

// The admin product filter uses its own vocabulary; App Management groups by
// device kind, so lift/stair/ceiling have to be translated for the device
// picker. Ceiling was previously undefined, which left its Targeting dialog's
// device list unfiltered — easy to target the wrong product's device.
const PRODUCT_FILTER: Record<string, string | undefined> = {
  stair: 'm8',
  lift: 'cop-lop',
  ceiling: 'avira',
  global: undefined,
};

const emptyTargets: AppFeatureTargets = { devices: [], roles: [], users: [] };

/**
 * Ceiling-specific app settings (key/value — not visibility flags). First
 * setting: how long a soft-deleted ceiling pattern stays restorable in the
 * app before being permanently purged. super_admin writes; other staff get a
 * 403 from the PATCH, surfaced via onError.
 */
function CeilingSettingsCard({ onError }: { onError: (msg: string) => void }) {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ['app-settings'], queryFn: getAppSettings });
  const [days, setDays] = useState<string>('');

  const stored = data?.settings.ceilingPatternRetentionDays ?? 30;
  useEffect(() => setDays(String(stored)), [stored]);

  const saveMutation = useMutation({
    mutationFn: () =>
      updateAppSettings({ ceilingPatternRetentionDays: Number(days) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['app-settings'] }),
    onError: (e: unknown) =>
      onError((e as { message?: string }).message ?? 'Failed to save settings'),
  });

  const parsed = Number(days);
  const valid = Number.isInteger(parsed) && parsed >= 1 && parsed <= 365;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 mt-6">
      <div className="px-4 py-3 border-b border-gray-100">
        <h3 className="text-sm font-semibold text-gray-900">Ceiling settings</h3>
      </div>
      <div className="px-4 py-4 flex items-center gap-4">
        <div className="flex-1">
          <p className="text-sm text-gray-800">Deleted pattern retention</p>
          <p className="text-xs text-gray-500 mt-0.5">
            Days a deleted pattern stays restorable in the app before permanent
            deletion. Default 30. Applies from the next app sync and the next
            setup save.
          </p>
        </div>
        <input
          type="number"
          min={1}
          max={365}
          value={days}
          onChange={(e) => setDays(e.target.value)}
          className="w-24 rounded-lg border border-gray-300 px-3 py-1.5 text-sm text-right"
        />
        <span className="text-xs text-gray-500">days</span>
        <button
          onClick={() => valid && saveMutation.mutate()}
          disabled={!valid || saveMutation.isPending || parsed === stored}
          className="rounded-lg bg-indigo-600 px-4 py-1.5 text-sm font-medium text-white disabled:opacity-40"
        >
          {saveMutation.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

export function AppManagement() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<(typeof TABS)[number]['key']>('stair');
  const [editing, setEditing] = useState<AdminAppFeature | null>(null);
  const [err, setErr] = useState('');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['app-features'],
    queryFn: getAppFeatures,
  });

  const toggleMutation = useMutation({
    mutationFn: (feature: AdminAppFeature) =>
      updateAppFeature(feature.key, { visible: !feature.visible }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['app-features'] }),
    onError: (e: unknown) => setErr((e as { message?: string }).message ?? 'Failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: (key: string) => deleteAppFeature(key),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['app-features'] }),
    onError: (e: unknown) => setErr((e as { message?: string }).message ?? 'Failed'),
  });

  const latestAppVersion = data?.latestAppVersion ?? null;

  const grouped = useMemo(() => {
    const byScreen = new Map<string, AdminAppFeature[]>();
    for (const feature of (data?.features ?? []).filter((f) => f.product === tab)) {
      const list = byScreen.get(feature.screen) ?? [];
      list.push(feature);
      byScreen.set(feature.screen, list);
    }
    return [...byScreen.entries()];
  }, [data, tab]);

  function onToggle(feature: AdminAppFeature) {
    setErr('');
    // Emergency and Stop are how someone gets out of a stuck lift — make
    // hiding one a deliberate act, not a stray click.
    if (feature.visible && feature.critical) {
      const ok = window.confirm(
        `"${feature.label}" is a safety control. Hiding it removes it from the app for the targeted users. Continue?`,
      );
      if (!ok) return;
    }
    toggleMutation.mutate(feature);
  }

  return (
    <div className="p-8">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-1">App Management</h2>
          <p className="text-sm text-gray-500">
            Show or hide mobile-app features and screens. Changes apply the next time a user
            opens the app — no app update needed.
          </p>
        </div>
        {latestAppVersion && (
          <span className="text-xs text-gray-400">Latest app build: {latestAppVersion}</span>
        )}
      </div>

      {err && (
        <div className="mb-4 rounded-lg bg-red-50 border border-red-200 px-4 py-2 text-sm text-red-700">
          {err}
        </div>
      )}

      <div className="flex border-b border-gray-200 mb-6">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px ${
              tab === t.key
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {isLoading && <p className="text-sm text-gray-500">Loading…</p>}
      {isError && <p className="text-sm text-red-600">Failed to load feature flags.</p>}

      {!isLoading && grouped.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-4 py-10 text-center">
          <p className="text-sm text-gray-500">No features registered for this product yet.</p>
          <p className="text-xs text-gray-400 mt-1">
            The mobile app registers its features on startup — open the app once against this
            environment and they will appear here.
          </p>
        </div>
      )}

      {tab === 'ceiling' && <CeilingSettingsCard onError={setErr} />}

      <div className="space-y-6">
        {grouped.map(([screen, rows]) => (
          <div
            key={screen}
            className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
          >
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
              <h3 className="text-sm font-semibold text-gray-700">{screen}</h3>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-left text-xs uppercase tracking-wide">
                <tr>
                  <th className="px-4 py-2 font-medium">Feature</th>
                  <th className="px-4 py-2 font-medium">Applies to</th>
                  <th className="px-4 py-2 font-medium">Status</th>
                  <th className="px-4 py-2 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((feature) => {
                  const stale =
                    latestAppVersion !== null &&
                    feature.lastRegisteredAppVersion !== latestAppVersion;
                  return (
                    <tr key={feature.key} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-gray-900">{feature.label}</span>
                          {feature.kind === 'screen' && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                              Screen
                            </span>
                          )}
                          {feature.critical && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-50 text-red-700 border border-red-200">
                              Safety
                            </span>
                          )}
                          {stale && (
                            <span
                              className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200"
                              title="Not present in the latest app build"
                            >
                              Stale
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5">{feature.key}</p>
                      </td>
                      <td className="px-4 py-3">
                        <TargetSummary feature={feature} />
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => onToggle(feature)}
                          disabled={toggleMutation.isPending}
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            feature.visible
                              ? 'bg-green-100 text-green-700'
                              : 'bg-gray-100 text-gray-500'
                          }`}
                        >
                          {feature.visible ? 'Visible' : 'Hidden'}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button
                          onClick={() => {
                            setErr('');
                            setEditing(feature);
                          }}
                          className="text-indigo-600 hover:text-indigo-700 text-xs font-medium"
                        >
                          Targeting
                        </button>
                        {stale && (
                          <button
                            onClick={() => {
                              if (window.confirm(`Remove "${feature.key}" from App Management?`)) {
                                deleteMutation.mutate(feature.key);
                              }
                            }}
                            className="ml-3 text-red-600 hover:text-red-700 text-xs font-medium"
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ))}
      </div>

      {editing && (
        <TargetingModal
          feature={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            qc.invalidateQueries({ queryKey: ['app-features'] });
          }}
          onError={setErr}
        />
      )}
    </div>
  );
}

function TargetSummary({ feature }: { feature: AdminAppFeature }) {
  const { devices, roles, users } = feature.targets;
  const total = devices.length + roles.length + users.length;

  if (feature.visible) return <span className="text-xs text-gray-400">Everyone</span>;
  if (total === 0) return <span className="text-xs text-gray-600">Hidden for everyone</span>;

  const parts = [
    devices.length && `${devices.length} device${devices.length > 1 ? 's' : ''}`,
    roles.length && `${roles.length} role${roles.length > 1 ? 's' : ''}`,
    users.length && `${users.length} user${users.length > 1 ? 's' : ''}`,
  ].filter(Boolean) as string[];

  return (
    <span className="text-xs text-gray-600">
      {feature.targetingMode === 'hide_selected' ? 'Hidden for' : 'Visible only to'}{' '}
      {parts.join(', ')}
    </span>
  );
}

function TargetingModal({
  feature,
  onClose,
  onSaved,
  onError,
}: {
  feature: AdminAppFeature;
  onClose: () => void;
  onSaved: () => void;
  onError: (message: string) => void;
}) {
  const [mode, setMode] = useState<AppFeatureTargetingMode>(feature.targetingMode);
  const [targets, setTargets] = useState<AppFeatureTargets>(feature.targets ?? emptyTargets);
  const [deviceSearch, setDeviceSearch] = useState('');
  const [userSearch, setUserSearch] = useState('');

  const productFilter = PRODUCT_FILTER[feature.product];
  const deviceParams = {
    ...(deviceSearch ? { search: deviceSearch } : {}),
    ...(productFilter ? { product: productFilter } : {}),
  };
  const { data: deviceData, isFetching: devicesLoading } = useQuery({
    queryKey: ['adminDevices', deviceParams],
    queryFn: () => getAdminDevices(deviceParams),
  });
  const { data: userData, isFetching: usersLoading } = useQuery({
    queryKey: ['users', userSearch],
    queryFn: () => getUsers(userSearch ? { search: userSearch } : undefined),
  });

  const saveMutation = useMutation({
    mutationFn: () => updateAppFeature(feature.key, { targetingMode: mode, targets }),
    onSuccess: onSaved,
    onError: (e: unknown) => onError((e as { message?: string }).message ?? 'Failed'),
  });

  const deviceOptions: MultiSelectOption[] = (deviceData?.devices ?? []).map((d) => {
    const sublabel = [d.typeName, d.ownerMobile].filter(Boolean).join(' · ');
    return { id: d.id, label: d.serialNumber, ...(sublabel ? { sublabel } : {}) };
  });
  const userOptions: MultiSelectOption[] = (userData?.users ?? []).map((u) => ({
    id: u.id,
    label: u.name ?? u.mobile,
    sublabel: [u.mobile, u.role].filter(Boolean).join(' · '),
  }));

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Targeting</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl leading-none">
            ×
          </button>
        </div>

        <p className="text-sm text-gray-600 mb-1">{feature.label}</p>
        <p className="text-xs text-gray-400 mb-4">{feature.key}</p>

        {feature.visible && (
          <div className="mb-4 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
            This feature is currently visible to everyone. Targeting only takes effect once you
            set it to Hidden — and saving while it is visible clears the targets.
          </div>
        )}

        <div className="space-y-3 mb-4">
          <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer select-none">
            <input
              type="radio"
              checked={mode === 'hide_selected'}
              onChange={() => setMode('hide_selected')}
              className="w-4 h-4 accent-indigo-600 mt-0.5"
            />
            <span>
              Hide for the selected
              <span className="block text-xs text-gray-400">
                Everyone else keeps the feature. Nothing selected = hidden for everyone.
              </span>
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer select-none">
            <input
              type="radio"
              checked={mode === 'show_selected'}
              onChange={() => setMode('show_selected')}
              className="w-4 h-4 accent-indigo-600 mt-0.5"
            />
            <span>
              Show only to the selected
              <span className="block text-xs text-gray-400">
                Everyone else loses it — useful for testing with a small group.
              </span>
            </span>
          </label>
        </div>

        <div className="space-y-4">
          <MultiSelectDropdown
            label="Devices"
            options={deviceOptions}
            selected={targets.devices}
            onChange={(devices) => setTargets((t) => ({ ...t, devices }))}
            onSearch={setDeviceSearch}
            loading={devicesLoading}
            placeholder="Search serial…"
          />
          <MultiSelectDropdown
            label="Roles"
            options={ROLE_OPTIONS}
            selected={targets.roles}
            onChange={(roles) => setTargets((t) => ({ ...t, roles: roles as typeof t.roles }))}
          />
          <MultiSelectDropdown
            label="Users"
            options={userOptions}
            selected={targets.users}
            onChange={(users) => setTargets((t) => ({ ...t, users }))}
            onSearch={setUserSearch}
            loading={usersLoading}
            placeholder="Search name or mobile…"
          />
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={() => saveMutation.mutate()}
            disabled={saveMutation.isPending}
            className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
          >
            {saveMutation.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
